import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Package } from '../../types/Package';
import { formatCurrency } from '../../utils/CurrencyUtils';
import { Category, loadCategoriesFromServer } from '../../utils/categories';
import { parsePackageFinderParams } from '../../utils/packageFinderShare';
import './PackageFinderWizard.css';
import InquiryDatePicker from '../Common/InquiryDatePicker';
import { inquiryDateLine, useInquiryDate } from '../../utils/inquiryDate';

interface WizardAnswers {
  category?: string;
}

interface PackageMatch {
  package: Package;
  score: number;
  matchPercentage: number;
  reasons: string[];
  matchedCategory?: string; // Category name that matched the search
}

interface PackageFinderWizardProps {
  packages: Package[];
  isOpen: boolean;
  onClose: () => void;
  onPackageSelect: (packageItem: Package, categoryFilter?: string | null) => void;
}

// Option type definitions
interface BaseOption {
  id: string;
  label: string;
  icon: string;
}

interface CategoryOption extends BaseOption {
  color: string;
}

interface DurationOption extends BaseOption {
  description: string;
  min: number;
  max: number;
}

interface LocationOption extends BaseOption {
  keywords: string[];
}

interface PeopleOption extends BaseOption {
  description?: string;
  keywords: string[];
}

type QuestionOption = CategoryOption | DurationOption | LocationOption | PeopleOption;

interface Question {
  id: string;
  title: string;
  subtitle: string;
  options: QuestionOption[];
}

const PackageFinderWizard: React.FC<PackageFinderWizardProps> = ({
  packages,
  isOpen,
  onClose,
  onPackageSelect
}) => {
  useInquiryDate(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<WizardAnswers>({});
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    email: 'hello@carmenlorraine.com',
    phone: '+27 (0) 82 123 4567'
  });
  const [galleryCategories, setGalleryCategories] = useState<Category[]>([]);

  // Helper function to get CSS variable value
  const getCSSVariable = (variableName: string, fallback: string = '#8B4513'): string => {
    if (typeof window === 'undefined') return fallback;
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(variableName).trim();
    return value || fallback;
  };

  const totalSteps = 1;

  // Load contact info from localStorage
  useEffect(() => {
    const savedContent = localStorage.getItem('website_content');
    if (savedContent) {
      try {
        const parsed = JSON.parse(savedContent);
        if (parsed.contactEmail) setContactInfo(prev => ({ ...prev, email: parsed.contactEmail }));
        if (parsed.contactPhone) setContactInfo(prev => ({ ...prev, phone: parsed.contactPhone }));
      } catch (error) {
        console.error('Error loading contact info:', error);
      }
    }
  }, []);

  // Pre-fill answers from URL parameters when wizard opens
  useEffect(() => {
    if (isOpen) {
      const params = parsePackageFinderParams();
      const preFilledAnswers: WizardAnswers = {};
      
      if (params.category) {
        preFilledAnswers.category = params.category;
      }
      
      // Note: Duration, location, and people can be added here if needed
      // For now, we'll just handle category as it's the most common use case
      
      if (Object.keys(preFilledAnswers).length > 0) {
        setAnswers(preFilledAnswers);
      }
    }
  }, [isOpen]);

  // Load gallery categories when wizard opens and listen for updates
  useEffect(() => {
    if (isOpen) {
      const loadCategories = async () => {
        try {
          const categories = await loadCategoriesFromServer();
          setGalleryCategories(categories);
        } catch (error) {
          console.error('Error loading categories:', error);
          setGalleryCategories([]);
        }
      };
      loadCategories();
      
      // Listen for category updates
      const handleCategoryUpdate = () => {
        loadCategories();
      };
      
      window.addEventListener('categories_updated', handleCategoryUpdate);
      window.addEventListener('storage', (e) => {
        if (e.key === 'admin_categories' || e.key === null) {
          loadCategories();
        }
      });
      
      return () => {
        window.removeEventListener('categories_updated', handleCategoryUpdate);
      };
    }
  }, [isOpen]);

  // Generate prepopulated message based on search answers
  const generateContactMessage = (): string => {
    const parts: string[] = [];
    
    parts.push('Hi Carmen,');
    parts.push('');
    parts.push('I used your Package Finder but couldn\'t find exactly what I\'m looking for. Here are my preferences:');
    parts.push('');
    
    if (answers.category) {
      const categoryOption = questions[0].options.find(o => o.id === answers.category);
      parts.push(`📸 Type of shoot: ${categoryOption?.label || answers.category}`);
    }
    
    
    parts.push('');
    parts.push('Could you help me find a package that matches my needs? I\'d love to discuss my photography requirements.');
    parts.push('');
    const dateLine = inquiryDateLine();
    if (dateLine) {
      parts.push(dateLine);
      parts.push('');
    }
    parts.push('Looking forward to hearing from you!');
    
    return parts.join('\n');
  };

  // Format phone number for WhatsApp (remove spaces, parentheses, etc.)
  const formatPhoneForWhatsApp = (phone: string): string => {
    return phone.replace(/[\s\(\)\-]/g, '').replace(/^\+/, '');
  };

  // Generate WhatsApp URL
  const getWhatsAppUrl = (): string => {
    const message = encodeURIComponent(generateContactMessage());
    const phone = formatPhoneForWhatsApp(contactInfo.phone);
    return `https://wa.me/${phone}?text=${message}`;
  };

  // Generate Email mailto URL
  const getEmailUrl = (): string => {
    const subject = encodeURIComponent('Package Finder Inquiry');
    const body = encodeURIComponent(generateContactMessage());
    return `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  // Map wizard category ID to category slug
  // This function tries to find a category that matches the wizard category ID (like "Wedding", "Portrait")
  // by checking both the category name and slug
  const mapCategoryIdToSlug = useMemo(() => {
    const mapper = (categoryId: string): string => {
      if (!categoryId) return '';
      
      const normalizedId = categoryId.toLowerCase().trim();
      
      // First, try to find by exact name match (case-insensitive)
      const categoryByName = galleryCategories.find(
        cat => cat.name.toLowerCase().trim() === normalizedId
      );
      if (categoryByName) {
        console.log(`🔍 mapCategoryIdToSlug: Found "${categoryId}" by name -> slug: "${categoryByName.slug}"`);
        return categoryByName.slug;
      }

      // Then, try to find by slug match (in case the ID is already a slug)
      const categoryBySlug = galleryCategories.find(
        cat => cat.slug.toLowerCase().trim() === normalizedId
      );
      if (categoryBySlug) {
        console.log(`🔍 mapCategoryIdToSlug: Found "${categoryId}" by slug -> slug: "${categoryBySlug.slug}"`);
        return categoryBySlug.slug;
      }

      // If no match found, generate a slug from the category ID (fallback for backward compatibility)
      // This handles cases where categories haven't been loaded yet or don't exist in the system
      const generatedSlug = normalizedId.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      console.log(`🔍 mapCategoryIdToSlug: No match for "${categoryId}", generated slug: "${generatedSlug}"`);
      return generatedSlug;
    };
    return mapper;
  }, [galleryCategories]);

  // Map category slug to category name
  const mapCategorySlugToName = useMemo(() => {
    const mapper = (slug: string): string | null => {
      const category = galleryCategories.find(
        cat => cat.slug.toLowerCase() === slug.toLowerCase()
      );
      return category ? category.name : null;
    };
    return mapper;
  }, [galleryCategories]);

  // Generate category options dynamically from gallery categories
  // Only include categories that have at least one active package
  const categoryOptions = useMemo(() => {
    // Get all active packages
    const activePackages = packages.filter(p => p.available !== false);
    
    // Filter categories to only include those with active packages
    const categoriesWithPackages = galleryCategories.filter(cat => {
      const categoryName = cat.name.toLowerCase().trim();
      const categorySlug = cat.slug.toLowerCase().trim();
      
      // Check if any active package matches this category
      return activePackages.some(pkg => {
        // Check categories array
        if (pkg.categories && pkg.categories.length > 0) {
          return pkg.categories.some(catSlug => 
            catSlug.toLowerCase().trim() === categorySlug
          );
        }
        // Check single category field
        if (pkg.category) {
          const pkgCategory = pkg.category.toLowerCase().trim();
          return pkgCategory === categoryName || pkgCategory === categorySlug;
        }
        return false;
      });
    });
    
    const dynamicOptions: CategoryOption[] = categoriesWithPackages.map(cat => {
      // Map category names to icons and colors
      const iconMap: { [key: string]: string } = {
        'wedding': 'fas fa-heart',
        'portrait': 'fas fa-user-circle',
        'portraits': 'fas fa-user-circle',
        'family': 'fas fa-users',
        'business': 'fas fa-briefcase',
        'event': 'fas fa-calendar-alt',
        'events': 'fas fa-calendar-alt',
        'newborn': 'fas fa-baby',
        'engagement': 'fas fa-ring',
        'maternity': 'fas fa-baby-carriage',
        'corporate': 'fas fa-building',
        'commercial': 'fas fa-briefcase',
      };
      
      const defaultIcon = 'fas fa-camera';
      const categoryKey = cat.slug.toLowerCase().trim();
      const categoryName = cat.name.toLowerCase().trim();
      
      // Try to find icon by slug first, then by name
      let icon = iconMap[categoryKey];
      if (!icon) {
        icon = iconMap[categoryName];
      }
      // If still not found, try matching partial strings
      if (!icon) {
        for (const [key, value] of Object.entries(iconMap)) {
          if (categoryKey.includes(key) || categoryName.includes(key) || 
              key.includes(categoryKey) || key.includes(categoryName)) {
            icon = value;
            break;
          }
        }
      }
      // Fallback to default
      if (!icon) {
        icon = defaultIcon;
      }
      
      return {
        id: cat.name, // Use category name as ID for matching
        label: cat.name,
        icon: icon,
        color: cat.color || getCSSVariable('--color-primary', '#8B4513')
      };
    });
    
    // Add "No Preference" option
    dynamicOptions.push({ id: 'any', label: 'No Preference', icon: 'fas fa-question-circle', color: '#6C757D' });
    
    return dynamicOptions;
  }, [galleryCategories, packages]);

  // Question configurations - simplified to just category
  const questions: Question[] = useMemo(() => [
    {
      id: 'category',
      title: 'What type of shoot are you looking for?',
      subtitle: 'Select the category that best matches your needs',
      options: categoryOptions
    }
  ], [categoryOptions]);

  // Score all packages based on answers - show ALL packages, use search criteria for scoring/ordering
  const matchedPackages = useMemo(() => {
    // Require category to be answered
    if (!answers.category) return [];
    
    // Log if categories are not loaded yet (this might affect matching)
    if (galleryCategories.length === 0) {
      console.warn('⚠️ Package Finder: Categories not loaded yet. Matching may be less accurate.');
    }

    // Start with ALL available (active) packages - we'll score them all, not filter
    let allPackages: Package[] = packages.filter(p => p.available !== false);
    console.log(`🔍 Package Finder: Scoring all ${allPackages.length} active packages based on search criteria`);

    // Calculate match scores for ALL packages (not filtered - show all packages)
    // Packages are scored based on how well they match the search criteria
    const matches: PackageMatch[] = allPackages.map(pkg => {
      let score = 0;
      let maxScore = 0;
      const reasons: string[] = [];
      let matchedCategoryName: string | undefined;

      // Category match - check if package matches the selected category
      if (answers.category && answers.category !== 'any') {
        maxScore += 20;
        
        const selectedCategoryName = answers.category;
        const generateSlug = (name: string): string => {
          return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        };
        
        let categoryMatches = false;
        
        // Generate possible slugs to match
        const matchingGalleryCategory = galleryCategories.find(c => 
          c.name.toLowerCase().trim() === selectedCategoryName.toLowerCase().trim()
        );
        const generatedSlug = generateSlug(selectedCategoryName);
        const normalizedCategoryName = selectedCategoryName.toLowerCase().trim();
        
        const possibleSlugs = [
          matchingGalleryCategory?.slug.toLowerCase().trim(),
          generatedSlug,
          normalizedCategoryName
        ].filter(Boolean) as string[];
        
        // Check if package matches category
        if (pkg.categories && pkg.categories.length > 0) {
          categoryMatches = pkg.categories.some(catSlug => 
            possibleSlugs.includes(catSlug.toLowerCase().trim())
          );
        } else if (pkg.category) {
          categoryMatches = possibleSlugs.includes(pkg.category.toLowerCase().trim()) || 
            pkg.category.toLowerCase().trim() === normalizedCategoryName;
        }
        
        if (categoryMatches) {
          score += 20;
          // Determine which category matched for display
          if (pkg.categories && pkg.categories.length > 0) {
            const matchedSlug = pkg.categories.find(catSlug => 
              possibleSlugs.includes(catSlug.toLowerCase().trim())
            );
            if (matchedSlug) {
              const categoryFromGallery = galleryCategories.find(c => 
                c.slug.toLowerCase().trim() === matchedSlug.toLowerCase().trim()
              );
              matchedCategoryName = categoryFromGallery?.name || matchedSlug;
            }
          } else if (pkg.category) {
            matchedCategoryName = pkg.category;
          }
          reasons.push('Category match');
        }
      } else if (answers.category === 'any') {
        maxScore += 20;
        score += 20;
        reasons.push('Category flexible');
      }


      const matchPercentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 100;

      return {
        package: pkg,
        score,
        matchPercentage,
        reasons,
        matchedCategory: matchedCategoryName
      };
    });

    // Sort by match percentage (descending), then by score
    const sortedMatches = matches.sort((a, b) => {
      if (b.matchPercentage !== a.matchPercentage) {
        return b.matchPercentage - a.matchPercentage;
      }
      return b.score - a.score;
    });
    
    // Filter to only show packages that match ALL required criteria:
    // - Must be available (active)
    // - Category (unless "No Preference" selected)
    const requiredCriteria: string[] = [];
    if (answers.category && answers.category !== 'any') requiredCriteria.push('category');
    
    // Only return packages that match all required criteria and are active
    return sortedMatches.filter(match => {
      const pkg = match.package;
      
      // Must be available (active)
      if (pkg.available === false) {
        return false;
      }
      
      // Check if package matches all required criteria
      if (requiredCriteria.includes('category')) {
        // Must have a category match
        const matchesCategory = match.reasons.some(r => r.includes('Category match'));
        if (!matchesCategory) {
          return false;
        }
      }
      
      return true;
    });
  }, [packages, answers, questions, mapCategoryIdToSlug, mapCategorySlugToName, galleryCategories]);

  const handleAnswer = (questionId: string, optionId: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));

    // Auto-advance to next step
    if (currentStep < totalSteps - 1) {
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 300);
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleStartOver = () => {
    setCurrentStep(0);
    setAnswers({});
  };

  const handleClose = () => {
    setCurrentStep(0);
    setAnswers({});
    onClose();
  };

  const formatDuration = (minutes: number): string => {
    const hours = minutes / 60;
    if (hours === 0.5) return '½ hour';
    if (hours === 1.5) return '1½ hours';
    if (hours % 1 === 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${hours.toFixed(1)} hours`;
  };

  if (!isOpen) return null;

  const currentQuestion = questions[currentStep];
  const isLastStep = currentStep === totalSteps - 1;
  const hasAnswer = answers[currentQuestion.id as keyof WizardAnswers];
  // Show results only if we have all required answers (category, duration, location) AND people answer
  // Only require category to show results
  const showResults = isLastStep && answers.category;

  const modalContent = (
    <div className="package-finder-wizard-overlay" onClick={handleClose}>
      <div className="package-finder-wizard-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="wizard-close-btn" onClick={handleClose}>
          <i className="fas fa-times"></i>
        </button>

        {/* Header */}
        <div className={`wizard-header ${showResults ? 'wizard-header-results' : ''}`}>
          <h2 className="wizard-title">
            {showResults ? (matchedPackages.length > 0 ? 'Your Perfect Matches' : 'No Exact Matches') : 'Find Your Perfect Package'}
          </h2>
          <p className="wizard-subtitle">
            {showResults 
              ? matchedPackages.length > 0
                ? `I found ${matchedPackages.length} package${matchedPackages.length !== 1 ? 's' : ''} that match what you're looking for!`
                : `No packages match your exact type of shoot and duration requirements.`
              : `Step ${currentStep + 1} of ${totalSteps}`
            }
          </p>
          {showResults && matchedPackages.length > 0 && (
            <div className="wizard-search-criteria" style={{
              marginTop: '0.4rem',
              padding: '0.4rem 0.6rem',
              background: 'rgba(139, 69, 19, 0.05)',
              borderRadius: '6px',
              border: '1px solid rgba(139, 69, 19, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              flexWrap: 'nowrap'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flex: '1',
                minWidth: 0
              }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: getCSSVariable('--color-primary', '#8B4513'),
                  flexShrink: 0
                }}>
                  <i className="fas fa-filter" style={{ fontSize: '0.7rem' }}></i>
                  <span>Search:</span>
                </div>
                {answers.category && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    padding: '0.25rem 0.5rem',
                    background: 'white',
                    borderRadius: '4px',
                    border: '1px solid rgba(139, 69, 19, 0.2)',
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    <i className="fas fa-tag" style={{ color: getCSSVariable('--color-primary', '#8B4513'), fontSize: '0.65rem', flexShrink: 0 }}></i>
                    <span>{questions[0].options.find(o => o.id === answers.category)?.label || answers.category}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setCurrentStep(0)}
                style={{
                  fontSize: '0.65rem',
                  padding: '0.25rem 0.5rem',
                  background: 'white',
                  border: `1px solid ${getCSSVariable('--color-primary', '#8B4513')}33`,
                  borderRadius: '4px',
                  color: getCSSVariable('--color-primary', '#8B4513'),
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  transition: 'all 0.2s ease',
                  fontWeight: 500,
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = getCSSVariable('--color-primary', '#8B4513');
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = getCSSVariable('--color-primary', '#8B4513');
                }}
                title="Edit your search criteria"
              >
                <i className="fas fa-edit" style={{ fontSize: '0.6rem' }}></i>
                <span>Edit</span>
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className={`wizard-content ${showResults ? 'wizard-content-results' : ''}`}>
          {showResults ? (
            /* Results Screen */
            <div className="wizard-results">
              {matchedPackages.length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">
                    <i className="fas fa-search"></i>
                  </div>
                  <h3>No Exact Matches Found</h3>
                  <p>Let me tailor a package that suits your requirements! Let's discuss your specific needs and create the perfect solution for you.</p>
                  
                  {/* Contact Options - Show when no results */}
                  <div className="no-results-contact" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
                    <button 
                      className="btn-primary results-contact-btn"
                      onClick={() => setShowContactModal(true)}
                    >
                      <span>Let Me Tailor a Package for You</span>
                    </button>
                    <button 
                      className="btn-secondary results-contact-btn"
                      onClick={handleStartOver}
                    >
                      <i className="fas fa-redo"></i>
                      <span>Try Different Search</span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="results-grid">
                    {matchedPackages.map((match, index) => {
                      const thumb =
                        match.package.heroImage?.secure_url ||
                        (match.package.heroImage as any)?.url ||
                        match.package.galleryImages?.[0]?.secure_url ||
                        match.package.galleryImages?.[0]?.url ||
                        match.package.image ||
                        '/placeholder.jpg';
                      const primaryCategory =
                        match.matchedCategory ||
                        (Array.isArray(match.package.categories) && match.package.categories.length > 0
                          ? match.package.categories[0]
                          : match.package.category) ||
                        null;
                      const isSpecial = Boolean(match.package.isSpecial && match.package.specialPrice && match.package.price);

                      return (
                        <div 
                          key={match.package.id} 
                          className="result-card"
                          style={{ animationDelay: `${index * 0.1}s` }}
                          onClick={() => {
                            // Get the category slug from the selected category
                            const selectedCategoryName = answers.category;
                            const categorySlug = selectedCategoryName && selectedCategoryName !== 'any'
                              ? mapCategoryIdToSlug(selectedCategoryName)
                              : null;
                            // Pass the category filter to the parent
                            (onPackageSelect as any)(match.package, categorySlug);
                            handleClose();
                          }}
                        >
                          <div className="result-card-content">
                            <div className="result-card-top">
                              <h3 className="result-package-name">{match.package.name}</h3>
                            </div>

                            <p className="result-package-short">
                              {match.package.short || match.package.description?.substring(0, 120) || 'Photography package tailored to your needs.'}
                            </p>

                            <div className="result-meta">
                              {match.package.durationMinutes && (
                                <div className="meta-item meta-item-duration">
                                  <i className="fas fa-clock"></i>
                                  <span>{formatDuration(match.package.durationMinutes)}</span>
                                </div>
                              )}
                              {primaryCategory && (
                                <div className="meta-item meta-item-category">
                                  <i className="fas fa-tag"></i>
                                  <span>{primaryCategory}</span>
                                </div>
                              )}
                            </div>

                            <div className="result-thumb">
                              <img src={thumb} alt={match.package.name} loading="lazy" />
                            </div>

                            <div className="result-price-row">
                              {isSpecial && match.package.price && match.package.specialPrice ? (
                                <>
                                  <span className="result-price-strike">
                                    {formatCurrency(match.package.price, match.package.currency || 'R')}
                                  </span>
                                  <span className="result-price-main">
                                    {formatCurrency(match.package.specialPrice, match.package.currency || 'R')}
                                  </span>
                                  <span className="result-price-pill">Special</span>
                                </>
                              ) : match.package.price ? (
                                <span className="result-price-main">
                                  {formatCurrency(match.package.price, match.package.currency || 'R')}
                                </span>
                              ) : (
                                <span className="result-price-main">Price on request</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* "Not Found What You're Looking For" Card */}
                    <div 
                      className="result-card not-found-card"
                      style={{ animationDelay: `${matchedPackages.length * 0.1}s` }}
                      onClick={() => setShowContactModal(true)}
                    >
                      <div className="not-found-card-content">
                        <div className="not-found-icon">
                          <i className="fas fa-question-circle"></i>
                        </div>
                        <h3 className="not-found-title">Not Found What You're Looking For?</h3>
                        <p className="not-found-description">
                          Can't find the perfect package? Let's chat and create something custom for you!
                        </p>
                        <InquiryDatePicker />
                        <div className="not-found-contact-options">
                          <a
                            href={getWhatsAppUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="not-found-contact-btn whatsapp-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fab fa-whatsapp"></i>
                            <span>WhatsApp</span>
                          </a>
                          <a
                            href={getEmailUrl()}
                            className="not-found-contact-btn email-btn"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fas fa-envelope"></i>
                            <span>Email</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                </>
              )}
            </div>
          ) : (
            /* Question Screen */
            <div className="wizard-question">
              <div className="question-header">
                <h3 className="question-title">{currentQuestion.title}</h3>
                <p className="question-subtitle">{currentQuestion.subtitle}</p>
              </div>

              <div className="question-options">
                {currentQuestion.options.map((option) => {
                  const isSelected = answers[currentQuestion.id as keyof WizardAnswers] === option.id;
                  const hasColor = 'color' in option;
                  const hasDescription = 'description' in option;
                  return (
                    <button
                      key={option.id}
                      className={`option-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleAnswer(currentQuestion.id, option.id)}
                      style={hasColor ? { '--option-color': (option as CategoryOption).color } as React.CSSProperties : undefined}
                    >
                      <div className="option-icon">
                        <i className={option.icon}></i>
                      </div>
                      <div className="option-content">
                        <span className="option-label">{option.label}</span>
                        {hasDescription && (
                          <span className="option-description">{(option as DurationOption | PeopleOption).description}</span>
                        )}
                      </div>
                      {isSelected && (
                        <div className="option-check">
                          <i className="fas fa-check-circle"></i>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {!showResults && (
          <div className="wizard-footer">
            <div className="wizard-actions">
              {currentStep > 0 && (
                <button className="btn-secondary" onClick={handleBack}>
                  <i className="fas fa-arrow-left"></i>
                  <span>Back</span>
                </button>
              )}
              <button className="btn-skip" onClick={handleSkip}>
                Skip
              </button>
              {hasAnswer && (
                <button className="btn-primary" onClick={handleNext}>
                  <span>Next</span>
                  <i className="fas fa-arrow-right"></i>
                </button>
              )}
            </div>
          </div>
        )}

        {showResults && (
          <div className="wizard-footer">
            <button className="btn-secondary" onClick={handleStartOver}>
              <i className="fas fa-redo"></i>
              <span>Start Over</span>
            </button>
          </div>
        )}

        {/* Contact Modal */}
        {showContactModal && (
          <div className="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
            <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
              <button className="contact-modal-close" onClick={() => setShowContactModal(false)}>
                <i className="fas fa-times"></i>
              </button>
              <h3>Let Me Tailor a Package for You</h3>
              <p className="contact-modal-subtitle">Tell me what you need. I'll tailor a package for you. Choose how you'd like to reach me, and I'll put it together with you.</p>
              
              <InquiryDatePicker />
              <div className="contact-options">
                <a 
                  href={getWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-option-btn whatsapp-btn"
                  onClick={() => setShowContactModal(false)}
                >
                  <i className="fab fa-whatsapp"></i>
                  <div className="contact-option-content">
                    <span className="contact-option-title">WhatsApp</span>
                    <span className="contact-option-subtitle">Send a quick message</span>
                  </div>
                  <i className="fas fa-arrow-right"></i>
                </a>
                
                <a 
                  href={getEmailUrl()}
                  className="contact-option-btn email-btn"
                  onClick={() => setShowContactModal(false)}
                >
                  <i className="fas fa-envelope"></i>
                  <div className="contact-option-content">
                    <span className="contact-option-title">Email</span>
                    <span className="contact-option-subtitle">Send an email inquiry</span>
                  </div>
                  <i className="fas fa-arrow-right"></i>
                </a>
              </div>

              <div className="contact-message-preview">
                <p className="preview-label">Your message will include:</p>
                <div className="preview-content">
                  {answers.category && (
                    <div className="preview-item">
                      <i className="fas fa-check"></i>
                      <span>Type: {questions[0].options.find(o => o.id === answers.category)?.label || answers.category}</span>
                    </div>
                  )}
                  {answers.category && (
                    <div className="preview-item">
                      <i className="fas fa-check"></i>
                      <span>Category: {questions[0].options.find(o => o.id === answers.category)?.label || answers.category}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render modal using portal at document root to ensure it's above all other elements
  return createPortal(modalContent, document.body);
};

export default PackageFinderWizard;
